import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

import getSortFromUrl from './helpers/getSortFromUrl/getSortFromUrl';
import { useSortAndFilterConfigContext } from '../../context/SortAndFilterConfigContext';
import { SortMethods } from '../../../../../modules/sortAndFilter/config/SortAndFilterConfig';

interface Props {
    children: React.ReactNode;
    urlKey: string;
    onSortChange?: () => void;
}

const TreatmentSortStateContext = createContext({});
const TreatmentSortUpdaterContext = createContext({});

/**
 * React context that provides the sort and filter controls for the treatment list. A page config can be
 * loaded in. A global config is always loaded in. Treatments must be passed in so that they can be
 * filtered against.
 */
const TreatmentSortProvider = ({ urlKey, children, onSortChange }: Props) => {
    const config = useSortAndFilterConfigContext();

    const [sortMethod, setSortMethodState] = useState<SortMethods>(
        (getSortFromUrl(urlKey) as SortMethods) || config.sorting.default
    );

    // Abstracted setSortMethod so that we can pop an 'onSortChange' in it.
    const setSortMethod = useCallback((sortMethod: SortMethods) => {
        if (onSortChange) onSortChange();
        setSortMethodState(sortMethod);
    }, [onSortChange]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const params = new URLSearchParams(window.location.search);

        params.set(`sort.${urlKey}`, sortMethod);

        const url = window.location.hash ? `?${params.toString()}${window.location.hash}` : `?${params.toString()}`;

        window.history.replaceState(null, '', url);
    }, [urlKey, sortMethod]);

    return (
        <TreatmentSortStateContext.Provider value={sortMethod}>
            <TreatmentSortUpdaterContext.Provider value={setSortMethod}>{children}</TreatmentSortUpdaterContext.Provider>
        </TreatmentSortStateContext.Provider>
    );
};

export const useTreatmentSortStateContext = () => {
    const sortMethod = useContext(TreatmentSortStateContext) as SortMethods;

    if (sortMethod === undefined) {
        throw new Error('useTreatmentSortStateContext must be used within a TreatmentSortProvider.');
    }

    return sortMethod;
};

export const useTreatmentSortUpdaterContext = () => {
    const setSortMethod = useContext(TreatmentSortUpdaterContext) as React.Dispatch<React.SetStateAction<SortMethods>>;

    if (setSortMethod === undefined) {
        throw new Error('useTreatmentSortUpdaterContext must be used within a TreatmentSortProvider.');
    }

    return setSortMethod;
};

export default TreatmentSortProvider;
